import React, { useState, useEffect, useRef } from "react";
import { FaFileWord, FaFileCsv, FaFilePdf } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const DragAndDropImage = ({
  onImagesUpdate,
  handleMediaDelete,
  uploadedImages,
  accept = "image/*",
  showPic = 3,
}) => {
  const [images, setImages] = useState([]);
  const [visibleImages, setVisibleImages] = useState([]);
  const [imageIndex, setImageIndex] = useState(showPic);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [cropQueue, setCropQueue] = useState([]);
  const [currentCropImage, setCurrentCropImage] = useState(null);

  useEffect(() => {
    const sortedImages = [...uploadedImages].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    setImages(sortedImages);
    setVisibleImages(sortedImages.slice(0, showPic));
  }, [uploadedImages, showPic]);

  useEffect(() => {
    if (cropQueue.length > 0 && !currentCropImage) {
      const nextImage = cropQueue[0];
      setCurrentCropImage(nextImage);
      setCropQueue((prev) => prev.slice(1));
    }
  }, [cropQueue, currentCropImage]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addImages(e.dataTransfer.files);
  };

  const handleFileChange = (e) => {
    addImages(e.target.files);
    e.target.value = "";
  };

  const addImages = (files) => {
    if (!files.length) return;

    const allowedExtensions = accept
      .split(",")
      .map((ext) => ext.trim().toLowerCase());
    const newNonImages = [];
    const imageFiles = [];
    const errors = [];

    Array.from(files).forEach((file) => {
      const isAllowed = allowedExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );

      if (!isAllowed) {
        errors.push(`"${file.name}" is not an allowed file type.`);
      } else if (!images.some((img) => img.file?.name === file.name)) {
        if (file.type.startsWith("image/")) {
          imageFiles.push(file);
        } else {
          newNonImages.push({
            file,
            preview: null,
            name: file.name,
            url: null,
            created_at: new Date().toISOString(),
          });
        }
      }
    });

    if (errors.length) {
      alert(errors.join("\n"));
      return;
    }

    if (newNonImages.length > 0) {
      const updatedImages = [...newNonImages, ...images].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setImages(updatedImages);
      setVisibleImages(updatedImages.slice(0, imageIndex));
      onImagesUpdate?.(updatedImages);
    }

    if (imageFiles.length > 0) {
      setCropQueue((prev) => [...prev, ...imageFiles]);
    }
  };

  const CropModal = ({ imageFile, onCropComplete, onCancel }) => {
    const [crop, setCrop] = useState({
      unit: "px",
      width: 579,
      x: 0,
      y: 0,
      height: 325,
      aspect: 1,
    });

    const [src, setSrc] = useState(null);
    const imageRef = useRef(null);

    useEffect(() => {
      const reader = new FileReader();
      reader.onload = (e) => setSrc(e.target.result);
      reader.readAsDataURL(imageFile);
    }, [imageFile]);

    const getCroppedImg = async () => {
      if (!imageRef.current) return;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
      const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

      const pixelCrop = {
        x: (crop.x ?? 0) * scaleX,
        y: (crop.y ?? 0) * scaleY,
        width: crop.width * scaleX,
        height: crop.height * scaleY,
      };

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        imageRef.current,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, "image/jpeg");
      });
    };

    const handleCrop = async () => {
      const croppedBlob = await getCroppedImg();
      onCropComplete(croppedBlob);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white p-4 rounded-lg min-w-[50vw] max-w-[100vw] min-h-[50vh] max-h-[80vh] overflow-auto">
          {src && (
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(c) => setCrop(c)}
              keepSelection={true}
            >
              <img
                ref={imageRef}
                src={src}
                alt="Crop me"
                style={{
                  width: "calc(70vw - 100px)",
                  maxHeight: "calc(80vh - 100px)",
                }}
              />
            </ReactCrop>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleCrop}
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              Crop
            </button>
          </div>
        </div>
      </div>
    );
  };

  const removeImage = (index) => {
    const imageToDelete = visibleImages[index];
    if (imageToDelete.id) {
      handleMediaDelete(imageToDelete.id);
    } else {
      const updatedImages = images.filter((_, i) => i !== index);
      setImages(updatedImages);
      setVisibleImages(updatedImages.slice(0, imageIndex));
      onImagesUpdate?.(updatedImages);
    }
  };

  const handleViewMore = () => {
    const nextIndex = imageIndex + showPic;
    setImageIndex(nextIndex);
    setVisibleImages(images.slice(0, nextIndex));
  };

  const handleViewLess = () => {
    setImageIndex(showPic);
    setVisibleImages(images.slice(0, showPic));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "docx")
      return <FaFileWord className="text-blue-500 text-6xl" />;
    if (ext === "csv") return <FaFileCsv className="text-green-500 text-6xl" />;
    if (ext === "pdf") return <FaFilePdf className="text-red-500 text-6xl" />;
    return null;
  };

  return (
    <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
      {currentCropImage && (
        <CropModal
          imageFile={currentCropImage}
          onCropComplete={(croppedBlob) => {
            const fileName = currentCropImage.name;
            const croppedFile = new File([croppedBlob], fileName, {
              type: "image/jpeg",
            });
            const previewUrl = URL.createObjectURL(croppedFile);
            const newImage = {
              file: croppedFile,
              preview: previewUrl,
              name: fileName,
              url: null,
              created_at: new Date().toISOString(),
            };
            setImages((prev) => {
              const updated = [newImage, ...prev].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
              );
              onImagesUpdate?.(updated);
              return updated;
            });
            setVisibleImages((prev) => {
              const updated = [newImage, ...prev].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
              );
              return updated.slice(0, imageIndex);
            });
            setCurrentCropImage(null);
          }}
          onCancel={() => setCurrentCropImage(null)}
        />
      )}

      <div className="grid lg:grid-cols-4 md:grid-cols-3 grid-cols-1 gap-4 mt-4">
        {visibleImages.map((image, index) => (
          <div
            key={index}
            className="relative bg-white dark:bg-gray-900 rounded-lg overflow-hidden h-[222px]"
            style={{
              boxShadow:
                "rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px",
            }}
          >
            <button
              className="absolute top-2 flex justify-center items-center right-2 bg-red-500 text-white rounded-full p-1 text-sm h-[22px] w-[22px]"
              onClick={() => removeImage(index)}
            >
              <MdClose />
            </button>

            {image.preview ||
            (image.url && /\.(png|jpe?g|webp|gif)$/i.test(image.url)) ? (
              <img
                src={image.preview || image.url}
                alt={`preview-${index}`}
                className="w-full object-fill cursor-pointer h-full"
                onClick={() => setZoomedImage(image.preview || image.url)}
              />
            ) : (
              <>
                <div className="flex flex-col items-center p-4 bg-gray-200 rounded-lg">
                  {getFileIcon(image.name)}
                </div>
                <div className="flex flex-col items-center p-4 rounded-lg">
                  <p className="text-gray-700">{image.name}</p>
                  <a
                    href={image.url}
                    download={image.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 text-blue-500 underline cursor-pointer"
                  >
                    Download
                  </a>
                </div>
              </>
            )}
          </div>
        ))}
        <div className="border-2 text-info dark:text-grey border-dashed border-info/40 dark:border-gray-300/40 rounded-xl p-4 text-center flex flex-col justify-center items-center">
          <p className="">Drag and drop your files here</p>
          <p className=" mb-2">or</p>
          <input
            type="file"
            accept={accept}
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="fileInput"
          />
          <label
            htmlFor="fileInput"
            className="cursor-pointer font-medium dark:text-black dark:button-primary-gradient text-sm rounded-md bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e,#9e9e9e)] text-dark dark:button-primary-gradient px-4 py-2 mt-2"
          >
            Browse Files
          </label>
        </div>
      </div>

      {images.length > showPic && (
        <div className="mt-4 flex justify-end">
          {imageIndex < images.length ? (
            <button
              onClick={handleViewMore}
              className="inline-flex justify-center px-4 py-2 font-medium text-sm items-center gap-1 text-sm font-medium dark:text-black bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e,#9e9e9e)] text-dark dark:button-primary-gradient rounded-md focus:outline-none"
            >
              View More
            </button>
          ) : (
            <button
              onClick={handleViewLess}
              className="cursor-pointer inline-flex justify-center px-4 py-2 font-medium text-sm items-center gap-1 text-sm font-medium dark:text-black bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e,#9e9e9e)] text-dark dark:button-primary-gradient dark:button-primary-gradient rounded-md focus:outline-none"
            >
              View Less
            </button>
          )}
        </div>
      )}

      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt="Zoomed"
            className="max-w-full max-h-full"
          />
        </div>
      )}
    </div>
  );
};

export default DragAndDropImage;
